/**
 * Admin V3 — Global toast context + hook
 *
 * Single-slot global toast. Latest message replaces the previous.
 * Auto-dismiss after 4000 ms (M2B precedent).
 *
 * Usage:
 *   const { showSuccess, showError, showInfo } = useToast();
 *   showSuccess('Chauffeur assigné: ...');
 *
 * Mount the provider once in AdminV3Shell. Phase 1 does NOT trigger any
 * toast (placeholders make no mutations); the provider is in place so
 * subsequent module phases can show toasts immediately.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import Toast from '../components/Toast';

const TOAST_AUTO_DISMISS_MS = 4000;

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState(null);

  // Auto-dismiss
  useEffect(() => {
    if (!toast) return undefined;
    const tid = setTimeout(() => setToast(null), TOAST_AUTO_DISMISS_MS);
    return () => clearTimeout(tid);
  }, [toast]);

  const showSuccess = useCallback((message) => setToast({ type: 'success', message }), []);
  const showError   = useCallback((message) => setToast({ type: 'error', message }), []);
  const showInfo    = useCallback((message) => setToast({ type: 'info', message }), []);
  const clear       = useCallback(() => setToast(null), []);

  const value = useMemo(
    () => ({ showSuccess, showError, showInfo, clear, toast }),
    [showSuccess, showError, showInfo, clear, toast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast toast={toast} onClose={clear} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Defensive fallback: if useToast is called outside the provider,
    // return no-op functions so the consumer doesn't crash.
    return {
      showSuccess: () => {},
      showError:   () => {},
      showInfo:    () => {},
      clear:       () => {},
      toast:       null,
    };
  }
  return ctx;
};

export default useToast;
